import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });
const blankT = () => ({ name: "", location: "", text: "" });
const blankS = () => ({ value: "", label: "" });

export function SocialProofDialog({ open, onClose, pageId, content, onChanged }) {
  const [tests, setTests] = useState([blankT(), blankT(), blankT()]);
  const [stats, setStats] = useState([blankS(), blankS(), blankS(), blankS()]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = (content?.testimonials_verified ? content.testimonials : []) || [];
    const s = (content?.stats_verified ? content.stats : []) || [];
    setTests([0, 1, 2].map(i => ({ ...blankT(), ...(t[i] || {}) })));
    setStats([0, 1, 2, 3].map(i => ({ ...blankS(), ...(s[i] || {}) })));
    setConfirm(false);
  }, [open, content]);

  const upd = (setter, i, key, val) => setter(arr => arr.map((x, j) => (j === i ? { ...x, [key]: val } : x)));

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/pages/${pageId}/social-proof`, { testimonials: tests, stats, confirm }, { headers: authHeaders() });
      toast.success("Gespeichert – die Bereiche erscheinen jetzt auf deiner Webseite.");
      onChanged();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="social-proof-dialog">
        <DialogHeader>
          <DialogTitle>Kundenstimmen &amp; Kennzahlen</DialogTitle>
          <DialogDescription>Diese Bereiche erscheinen nur, wenn du echte Angaben einträgst. Leere Felder werden nicht angezeigt.</DialogDescription>
        </DialogHeader>

        <h3 className="font-semibold text-sm text-slate-900">Kundenstimmen (max. 3)</h3>
        {tests.map((t, i) => (
          <div key={i} className="grid gap-2 border border-slate-200 rounded-xl p-3">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Name, z. B. Maria K." value={t.name} onChange={e => upd(setTests, i, "name", e.target.value)} data-testid={`testimonial-name-${i}`} />
              <Input placeholder="Ort (optional)" value={t.location} onChange={e => upd(setTests, i, "location", e.target.value)} data-testid={`testimonial-location-${i}`} />
            </div>
            <Textarea placeholder="Originalaussage des Kunden" rows={2} value={t.text} onChange={e => upd(setTests, i, "text", e.target.value)} data-testid={`testimonial-text-${i}`} />
          </div>
        ))}

        <h3 className="font-semibold text-sm text-slate-900 mt-2">Kennzahlen (max. 4, nur belegbare Werte)</h3>
        <div className="grid gap-2">
          {stats.map((x, i) => (
            <div key={i} className="grid grid-cols-[110px_1fr] gap-2">
              <Input placeholder="Wert" value={x.value} onChange={e => upd(setStats, i, "value", e.target.value)} data-testid={`stat-value-${i}`} />
              <Input placeholder="Beschreibung, z. B. Jahre im Betrieb" value={x.label} onChange={e => upd(setStats, i, "label", e.target.value)} data-testid={`stat-label-${i}`} />
            </div>
          ))}
        </div>

        <label className="flex items-start gap-2 text-sm text-slate-700 mt-2">
          <input type="checkbox" checked={confirm} onChange={e => setConfirm(e.target.checked)} className="mt-1" data-testid="social-proof-confirm" />
          <span>Ich bestätige, dass alle Angaben echt und belegbar sind (z. B. tatsächliche Kundenstimmen mit Einverständnis).</span>
        </label>

        <Button onClick={save} disabled={busy} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="social-proof-save-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
